import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Api } from 'lavva.exalushome';
import { ExtaLifeService } from 'lavva.exalushome.extalife';
import {
  GlobalTimeParameters,
  StateOfTurnOnSwitchDeviceParameters,
} from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/Common/OnOffSwitchConfigurations/OnOffSwitchParameters';
import { IRdp21ConfigService } from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/Dimmer/IRdp21ConfigService';
import {
  InputOutputDimmerParameters,
  LightTransitionTime,
  MaxLightLevel,
  MinLightLevel,
} from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/Dimmer/Rdp21ConfigParameters';
import { Rdp21ConfigService } from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/Dimmer/Rdp21ConfigService';
import { IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import ArrowButton from '../../../../../../components/arrow-button';
import { useBackdropContext } from '../../../../../../context/backdrop';
import ConfigCustomPage from '../../../device-details/components/config-custom';
import { useConfigurationService } from '../../../device-details/hooks/use-configuration-service';
import { useDeviceUpdates } from '../../hooks/use-device-updates';
import EnablingTimeForm from '../on-off/enable-time';
import GlobalTimeForm from '../on-off/global-time';
import TurnOnBehaviourForm from '../on-off/turn-behaviour';
import BrighteningTimeForm from './brightening-time';
import DimmingTimeForm from './dimming-time';
import InputModeForm from './input-mode';
import MaxLevelForm from './max-level';
import MinLevelForm from './min-level';
import { initialConfig, Rdp21ConfigParams, Rdp21ParamsEnum } from './types';

type Rdp21ConfigFormProps = {
  device: IDevice;
  channel?: IDeviceChannel;
};

const Rdp21ConfigForm: React.FC<Rdp21ConfigFormProps> = ({ device, channel }) => {
  const { t } = useTranslation('device-info');
  const [config, setConfig] = useState<Rdp21ConfigParams>(initialConfig);
  const [open, setOpen] = useState<boolean>(false);
  const extaLifeService = Api.Get<ExtaLifeService>(ExtaLifeService.ServiceName);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const {
    handleGetSwitchParamsResponse,
    RemotesButton,
    DiagnosticButton,
    DiagnosticResultPopup,
    firmwareService,
    UserManualButton,
  } = useConfigurationService(device, channel);
  const { VersionDeviceButton, CheckUpdateDeviceButton } = useDeviceUpdates(device);

  const getDefaultParams = async (panel: Rdp21ParamsEnum) => {
    turnOnBackdrop();
    const configService = await extaLifeService.GetDeviceServiceByServiceTypeAsync<IRdp21ConfigService>(
      Rdp21ConfigService.ServiceName,
    );

    switch (panel) {
      case Rdp21ParamsEnum.DIMMING_TIME: {
        if (channel) {
          const data = await configService.GetDimmingTimeAsync(device, channel.Number);

          handleGetSwitchParamsResponse<LightTransitionTime>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as LightTransitionTime });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.BRIGHTENING_TIME: {
        if (channel) {
          const data = await configService.GetBrighteningTimeAsync(device, channel.Number);

          handleGetSwitchParamsResponse<LightTransitionTime>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as LightTransitionTime });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.MIN_LEVEL: {
        if (channel) {
          const data = await configService.GetMinLevelAsync(device, channel.Number);

          handleGetSwitchParamsResponse<MinLightLevel>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as MinLightLevel });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.MAX_LEVEL: {
        if (channel) {
          const data = await configService.GetMaxLevelAsync(device, channel.Number);

          handleGetSwitchParamsResponse<MaxLightLevel>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as MaxLightLevel });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.TURN_ON_BEHAVIOUR: {
        if (channel) {
          const data = await configService.GetTurnOnBehaviourAsync(device, channel.Number);

          handleGetSwitchParamsResponse<StateOfTurnOnSwitchDeviceParameters>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as StateOfTurnOnSwitchDeviceParameters });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.GLOBAL_TIME: {
        if (channel) {
          const data = await configService.GetGlobalTimeSettingsAsync(device, channel.Number);

          handleGetSwitchParamsResponse<GlobalTimeParameters>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as GlobalTimeParameters });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.ENABLE_TIME: {
        if (channel) {
          const data = await configService.GetTurnOnTimeAsync(device, channel.Number);

          handleGetSwitchParamsResponse<GlobalTimeParameters>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as GlobalTimeParameters });
            setOpen(true);
          });
        }
        break;
      }
      case Rdp21ParamsEnum.INPUT_MODE: {
        if (channel) {
          const data = await configService.GetDimmerInputOutputParamAsync(device, channel.Number);

          handleGetSwitchParamsResponse<InputOutputDimmerParameters>(data, () => {
            setConfig({ ...initialConfig, [panel]: data as InputOutputDimmerParameters });
            setOpen(true);
          });
        }
        break;
      }
      default:
        break;
    }

    turnOffBackdrop();
  };

  const handleOpen = (panel: Rdp21ParamsEnum) => getDefaultParams(panel);

  const handleBack = () => {
    setOpen(false);
    setConfig({ ...initialConfig });
  };

  const activeConfigName = useMemo(() => Object.keys(config).find((key) => config[key] !== null) || null, [config]);

  const renderCorrentForm = () => {
    if (!activeConfigName) return null;

    switch (activeConfigName) {
      case Rdp21ParamsEnum.DIMMING_TIME: {
        return channel ? (
          <DimmingTimeForm device={device} channel={channel} dimmingTime={config[activeConfigName]} />
        ) : null;
      }
      case Rdp21ParamsEnum.BRIGHTENING_TIME: {
        return channel ? (
          <BrighteningTimeForm device={device} channel={channel} brighteningTime={config[activeConfigName]} />
        ) : null;
      }
      case Rdp21ParamsEnum.MIN_LEVEL: {
        return channel ? <MinLevelForm device={device} channel={channel} minLevel={config[activeConfigName]} /> : null;
      }
      case Rdp21ParamsEnum.MAX_LEVEL: {
        return channel ? <MaxLevelForm device={device} channel={channel} maxLevel={config[activeConfigName]} /> : null;
      }
      case Rdp21ParamsEnum.INPUT_MODE: {
        return <InputModeForm device={device} inputMode={config[activeConfigName]} />;
      }
      case Rdp21ParamsEnum.TURN_ON_BEHAVIOUR: {
        return <TurnOnBehaviourForm device={device} turnOnBehaviour={config[activeConfigName]} />;
      }
      case Rdp21ParamsEnum.GLOBAL_TIME: {
        return <GlobalTimeForm device={device} timeParams={config[activeConfigName]} />;
      }
      case Rdp21ParamsEnum.ENABLE_TIME: {
        return <EnablingTimeForm device={device} timeParams={config[activeConfigName]} />;
      }
      default:
        return null;
    }
  };

  return (
    <>
      {open ? (
        <ConfigCustomPage handleBack={handleBack}>{renderCorrentForm()}</ConfigCustomPage>
      ) : (
        <>
          {channel ? (
            <>
              <ArrowButton
                title={t('exalus.params.OnOffParams.InputModeParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.INPUT_MODE)}
                className="m-b-24"
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.BrighteningTimeParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.BRIGHTENING_TIME)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.DimmingTimeParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.DIMMING_TIME)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.MinLevelParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.MIN_LEVEL)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.MaxLevelParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.MAX_LEVEL)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.TurnOnBehaviourParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.TURN_ON_BEHAVIOUR)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.GlobalTimeParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.GLOBAL_TIME)}
              />
              <ArrowButton
                className="m-b-24"
                title={t('exalus.params.OnOffParams.EnableTimeParams')}
                onClick={() => handleOpen(Rdp21ParamsEnum.ENABLE_TIME)}
              />
              {RemotesButton}
            </>
          ) : (
            <>
              {firmwareService && VersionDeviceButton}
              {firmwareService && CheckUpdateDeviceButton}
              {DiagnosticButton}
              {UserManualButton}
            </>
          )}
        </>
      )}
      {DiagnosticResultPopup}
    </>
  );
};

export default Rdp21ConfigForm;
